import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';

const validationSchema = Yup.object().shape({
  facilities: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Facility/Equipment name is required'),
      description: Yup.string()
        .min(10, 'Description must be at least 10 characters')
        .max(200, 'Description must not exceed 200 characters'),
      capacity: Yup.number()
        .min(0, 'Capacity must be a positive number')
        .nullable(),
      availability: Yup.string()
        .oneOf(['Available', 'Under Maintenance'], 'Invalid availability status')
    })
  )
});

const FacilitiesEquipment = ({ initialData = {}, onComplete, onBack, facilityId }) => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [staffList, setStaffList] = useState([]);

  // Fetch existing facility details if facilityId is provided
  useEffect(() => {
    const fetchFacilityDetails = async () => {
      try {
        const { data } = await axios.get('http://127.0.0.1:8000/hospital/facility');

        // const data = await response.json();
        console.log('API Response:', data);

        setStaffList(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        setError(error.message);
      }
    };
    fetchFacilityDetails();
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    capacity: '',
    availability: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async ({ setSubmitting }) => {
    try {
      const response = await axios.post('http://127.0.0.1:8000/hospital/facility', formData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 201) {
        setSuccess('Facility information submitted successfully!');
        onComplete(data, true); // Call the onComplete function with the response data
        setError(''); // Reset error on successful submission
      } else {
        throw new Error('Facility information submission failed. Please try again.');
      }
    } catch (error) {
      setError(error?.response?.data?.detail || 'Failed to submit facility information');
    }
  };

  return (
    <motion.div
      className={css(styles.container)}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className={css(styles.title)}>Facilities and Equipment Information</h2>
      {error && <div className={css(styles.error)}>{error}</div>}
      {success && <div className={css(styles.success)}>{success}</div>}
      <Formik
        initialValues={{
          facilities: initialData.facilities || [{
            name: '',
            description: '',
            capacity: '',
            availability: 'Available'
          }]
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isValid, dirty }) => (
          <Form className={css(styles.form)}>
            <div className={css(styles.fieldGroup)}>
              <label htmlFor='name' className={css(styles.label)}>
                Facility name
              </label>
              <input
                name='name'
                type='text'
                onChange={handleChange}
                className={css(styles.input)}
                required
              />
              {errors.name && touched.name && (
                <div className={css(styles.error)}>{errors.name}</div>
              )}
            </div>
            <div className={css(styles.fieldGroup)}>
              <label htmlFor='description' className={css(styles.label)}>
                Description
              </label>
              <input
                name='description'
                type='text'
                onChange={handleChange}
                className={css(styles.input)}
                required
              />
              {errors.description && touched.description && (
                <div className={css(styles.error)}>{errors.description}</div>
              )}
            </div>
            <div className={css(styles.fieldGroup)}>
              <label htmlFor='capacity' className={css(styles.label)}>
                Capacity
              </label>
              <input
                name='capacity'
                type='text'
                onChange={handleChange}
                className={css(styles.input)}
                required
              />
              {errors.capacity && touched.capacity && (
                <div className={css(styles.error)}>{errors.capacity}</div>
              )}
            </div>
            <div className={css(styles.fieldGroup)}>
              <label htmlFor='name' className={css(styles.label)}>
                Availability
              </label>
              <input
                name='availability'
                type='text'
                onChange={handleChange}
                className={css(styles.input)}
                required
              />
              {errors.availability && touched.availability && (
                <div className={css(styles.error)}>{errors.availability}</div>
              )}
            </div>
            {/* Buttons */}
            <div className={css(styles.buttonContainer)}>
              <motion.button
                type='submit'
                className={css(
                  styles.button,
                  styles.nextButton
                )}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSubmit}
              >
                Save
              </motion.button>
              <motion.button
                type='button'
                className={css(
                  styles.button,
                  styles.nextButton
                )}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if (!isValid) {
                    console.log('Form is invalid, staying on the same page.');
                    alert('Please fill in all required fields before proceeding.');
                    return;
                  }
                  console.log('Form is valid, navigating to the next section.');
                  onComplete(); // Ensure this function correctly updates the state for the next section
                }}
              >
                Next
              </motion.button>
              <motion.button
                type='button'
                className={css(styles.button, styles.backButton)}
                onClick={onBack}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Back
              </motion.button>
            </div>
          </Form>
        )}
      </Formik>
      <table className={css(styles.table)}>
        <thead>
          <tr className={css(styles.label)}>
            <th className={css(styles.th)}>Name</th>
            <th className={css(styles.th)}>Description</th>
            <th className={css(styles.th)}>Capacity</th>
            <th className={css(styles.th)}>Availability</th>
          </tr>
        </thead>
        <tbody>
          {Array.isArray(staffList) && staffList.map((values, index) => (
            <tr key={index}>
              <td className={css(styles.td)}>{values.name}</td>
              <td className={css(styles.td)}>{values.description}</td>
              <td className={css(styles.td)}>{values.capacity}</td>
              <td className={css(styles.td)}>{values.availability}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: '2rem'
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#2783F7'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  facilityContainer: {
    padding: '1rem',
    marginBottom: '1rem',
    border: '1px solid #e2e8f0',
    borderRadius: '5px'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '1rem'
  },
  label: {
    marginBottom: '0.5rem',
    fontSize: '1rem',
    color: '#4a5568'
  },
  input: {
    padding: '0.5rem',
    fontSize: '1rem',
    borderRadius: '5px',
    border: '1px solid #e2e8f0'
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '1rem'
  },
  button: {
    border: 'none',
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  nextButton: {
    backgroundColor: '#2783F7',
    color: 'white'
  },
  backButton: {
    backgroundColor: '#e2e8f0',
    color: '#4a5568'
  },
  disabledButton: {
    backgroundColor: '#a0aec0',
    cursor: 'not-allowed'
  },
  error: {
    color: 'red',
    margin: '1rem 0'
  },
  success: {
    color: 'green',
    margin: '1rem 0'
  },
  table: {
    width: '100%',
    marginTop: '1rem',
    borderCollapse: 'collapse'
  },
  th: {
    background: 'light grey',
    padding: '10px',
    textAlign: 'left',
    color: 'black'
  },
  td: {
    padding: '10px',
    borderBottom: '1px solid #ddd',
    color: 'black'
  }
});

export default FacilitiesEquipment;
